import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Archive, Trash2, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '../ui/use-toast';
import { Skeleton } from '../ui/skeleton';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';

interface Post {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  author: {
    student_name: string;
    tutor_name: string;
    email: string;
    avatar_url: string | null;
  };
  comment_count?: number;
  archived?: boolean;
  archived_at?: string;
  archived_by?: string;
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  author: {
    student_name: string;
    avatar_url: string | null;
  };
  user_id: string;
  archived?: boolean;
  archived_at?: string;
  archived_by?: string;
}

interface AdminCommunityFeedProps {
  onModerate: (postId: string, action: 'archive' | 'delete', userId: string, isComment?: boolean) => void;
  filterStatus?: string;
}

export const AdminCommunityFeed = ({ onModerate, filterStatus = 'active' }: AdminCommunityFeedProps) => {
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [commentsMap, setCommentsMap] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: posts = [], isLoading: isPostsLoading } = useQuery({
    queryKey: ['adminPosts'],
    queryFn: async () => {
      console.log('Fetching all posts');
      
      try {
        // Obtener todos los posts sin filtrar
        const { data: posts, error } = await supabase
          .from('community_posts')
          .select(`
            *,
            author:profiles!fk_user(
              student_name,
              tutor_name,
              email,
              avatar_url
            )
          `)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching posts:', error);
          throw error;
        }

        // Log detallado para debug
        console.log('Posts raw data:', posts);
        
        console.log('Posts summary:', { 
          total: posts?.length,
          archived: posts?.filter(p => p.archived).length,
          active: posts?.filter(p => !p.archived).length,
          sample: posts?.slice(0, 2).map(p => ({
            id: p.id,
            content: p.content.substring(0, 20) + '...',
            archived: p.archived,
            archived_at: p.archived_at
          }))
        });

        return posts || [];
      } catch (error) {
        console.error('Error in queryFn:', error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  const fetchComments = async (postId: string) => {
    if (commentsMap[postId]?.length > 0) {
      setExpandedPostId(expandedPostId === postId ? null : postId);
      return;
    }

    try {
      setLoadingComments(prev => ({ ...prev, [postId]: true }));
      const { data: comments, error } = await supabase
        .from('post_comments')
        .select(`
          *,
          author:profiles!post_comments_user_id_fkey(
            student_name,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setCommentsMap(prev => ({ ...prev, [postId]: comments || [] }));
      setExpandedPostId(postId);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron cargar los comentarios",
      });
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string, userId: string, content: string) => {
    if (!user?.id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No tienes permisos para realizar esta acción.",
      });
      return;
    }

    try {
      // 1. Eliminar el comentario
      const { error: deleteError } = await supabase
        .from('post_comments')
        .delete()
        .eq('id', commentId);

      if (deleteError) throw deleteError;

      // 2. Crear registro de moderación
      const { error: moderationError } = await supabase
        .from('moderation_logs')
        .insert([
          {
            post_id: postId,
            user_id: userId,
            action: 'delete_comment',
            reason: 'Comentario inapropiado',
            content: content,
            moderator_id: user.id
          }
        ]);

      if (moderationError) throw moderationError;

      // 3. Crear notificación para el usuario
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: userId,
            type: 'moderation',
            title: 'Comentario eliminado',
            message: 'Tu comentario ha sido eliminado por un moderador.',
            post_id: postId
          }
        ]);

      if (notificationError) {
        console.error('Error al crear notificación:', notificationError);
      }

      // 4. Actualizar la lista de comentarios
      const updatedComments = commentsMap[postId]?.filter(c => c.id !== commentId) || [];
      setCommentsMap(prev => ({
        ...prev,
        [postId]: updatedComments
      }));

      // 5. Invalidar consultas relevantes
      queryClient.invalidateQueries(['moderationHistory']);
      queryClient.invalidateQueries(['adminPosts']);

      toast({
        title: "Comentario eliminado",
        description: "El comentario ha sido eliminado y se ha notificado al usuario.",
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el comentario",
      });
    }
  };

  const handleModerate = async (postId: string, action: 'archive' | 'delete', userId: string, isComment?: boolean) => {
    try {
      if (action === 'archive') {
        if (isComment) {
          console.log('Archiving comment:', { postId, userId });
          const { data, error } = await supabase.rpc('archive_post_comment', {
            p_comment_id: postId,
            p_archived_by: user?.id
          });

          if (error) throw error;

          // Crear notificación
          await supabase.rpc('create_moderation_notification', {
            p_user_id: userId,
            p_title: 'Comentario archivado',
            p_message: 'Tu comentario ha sido archivado por un moderador.',
            p_reference_id: postId,
            p_metadata: { type: 'comment_archived' }
          });

          console.log('Comment archived successfully:', data);
        } else {
          console.log('Archiving post:', { postId, userId });
          const { data, error } = await supabase.rpc('archive_community_post', {
            p_post_id: postId,
            p_archived_by: user?.id
          });

          if (error) throw error;

          // Crear notificación
          await supabase.rpc('create_moderation_notification', {
            p_user_id: userId,
            p_title: 'Publicación archivada',
            p_message: 'Tu publicación ha sido archivada por un moderador.',
            p_reference_id: postId,
            p_metadata: { type: 'post_archived' }
          });

          console.log('Post archived successfully:', data);
        }

        // Invalidar la caché para refrescar los datos
        queryClient.invalidateQueries(['adminPosts']);
        
        toast({
          title: isComment ? 'Comentario archivado' : 'Publicación archivada',
          description: 'El contenido ha sido archivado exitosamente.',
        });
      }
    } catch (error) {
      console.error('Error in handleModerate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo realizar la acción.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      {isPostsLoading ? (
        // Loading skeletons...
        Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-start space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          </Card>
        ))
      ) : posts.length === 0 ? (
        <Card className="p-4">
          <p className="text-center text-gray-500">No hay publicaciones disponibles.</p>
        </Card>
      ) : (
        posts.map((post) => (
          <Card key={post.id} className={`p-4 ${post.archived ? 'bg-gray-50 border-l-4 border-l-yellow-500' : ''}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <Avatar>
                  <AvatarImage src={post.author?.avatar_url || undefined} />
                  <AvatarFallback>{post.author?.student_name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className="font-medium">{post.author?.student_name}</p>
                    {post.archived && (
                      <span className="text-sm px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                        Archivado
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {format(new Date(post.created_at), "d 'de' MMMM 'de' yyyy", { locale: es })}
                  </p>
                  <p className="mt-2">{post.content}</p>
                  {post.archived && (
                    <p className="mt-2 text-sm text-gray-500">
                      Archivado el {format(new Date(post.archived_at || ''), "d 'de' MMMM 'de' yyyy", { locale: es })}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => fetchComments(post.id)}
                >
                  <MessageCircle className="h-5 w-5" />
                </Button>
                {!post.archived && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onModerate(post.id, 'archive', post.user_id)}
                  >
                    <Archive className="h-5 w-5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onModerate(post.id, 'delete', post.user_id)}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Comentarios expandidos */}
            {expandedPostId === post.id && (
              <div className="mt-4 space-y-4">
                <AnimatePresence>
                  {loadingComments[post.id] ? (
                    <div className="space-y-4">
                      {Array.from({ length: 2 }).map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <div className="flex items-start space-x-4 ml-12">
                            <Skeleton className="h-8 w-8 rounded-full" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-1/4" />
                              <Skeleton className="h-4 w-1/2" />
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      {(commentsMap[post.id] || []).map((comment) => (
                        <div
                          key={comment.id}
                          className={`ml-12 p-3 rounded-lg ${
                            comment.archived ? 'bg-gray-50 border-l-2 border-l-yellow-500' : 'bg-gray-100'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <Avatar className="h-8 w-8">
                                <AvatarImage
                                  src={comment.author?.avatar_url || undefined}
                                />
                                <AvatarFallback>
                                  {comment.author?.student_name?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-medium text-sm">
                                    {comment.author?.student_name}
                                  </p>
                                  {comment.archived && (
                                    <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded">
                                      Archivado
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500">
                                  {format(
                                    new Date(comment.created_at),
                                    "d 'de' MMMM 'de' yyyy",
                                    { locale: es }
                                  )}
                                </p>
                                <p className="mt-1 text-sm">{comment.content}</p>
                                {comment.archived && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    Archivado el {format(new Date(comment.archived_at || ''), "d 'de' MMMM 'de' yyyy", { locale: es })}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onModerate(comment.id, 'delete', comment.user_id, true)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
};
